import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { $transferUpdateStatusStore } from '@lib/store';
import { RestApiError, TransferRequestStatusEnum, type TransferRequestResponse } from '@shared/api';
import { Button, OTPInput, Spinner, Text } from '@shared/widgets';

const OTP_LENGTH = 6;

interface FormData {
  otpCode: string;
}
const DEFAULT_VALUES: FormData = { otpCode: '' };

interface OTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: TransferRequestResponse;
  onSubmit?: (data?: TransferRequestResponse) => void;
  onCancel?: () => void;
}

export const OTPModal = (props: OTPModalProps) => {
  const { control, formState, handleSubmit, setError, reset, clearErrors } = useForm<FormData>({
    reValidateMode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
  });

  const { t } = useTranslation('payments');

  const closeHandler = () => {
    reset(DEFAULT_VALUES);
    props.onClose();
  };
  const cancelHandler = () => {
    reset(DEFAULT_VALUES);
    props.onCancel?.() || props.onClose();
  };
  const submitHandler = async (data: FormData) => {
    try {
      const response = await $transferUpdateStatusStore.events.updateStatus({
        id: props.data.id,
        otpCode: data.otpCode,
        status: TransferRequestStatusEnum.Completed,
      });
      props.onSubmit?.(response.data);
      closeHandler();
    } catch (error) {
      if (RestApiError.isRestApiError(error)) {
        if (error.message === 'Invalid code, try again') {
          setError('otpCode', { message: t(`errors.api.${error.message}`, error.message) });
          return;
        }
      }
      closeHandler();
    }
  };

  if (!props.isOpen) return;
  return (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50'>
      <Spinner show={formState.isSubmitting} position />
      <div className='flex w-full max-w-lg flex-col gap-6 bg-ice p-6 shadow-lg dark:bg-soft-black'>
        <Text center size='3xl'>
          {t('modals.otp.title')}
        </Text>
        <form className='flex flex-col gap-10' noValidate onSubmit={handleSubmit(submitHandler)}>
          <Controller
            name='otpCode'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              validate: (value) => {
                if (value?.length !== OTP_LENGTH) return t('errors.form.required', { ns: 'common' });
                return true;
              },
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OTPInput
                className='mx-auto'
                length={OTP_LENGTH}
                error={error?.message}
                value={value}
                onChange={(value) => {
                  clearErrors('otpCode');
                  onChange(value);
                }}
              />
            )}
          />
          <div className='flex gap-3'>
            <Button fullWidth color='outlined' disabled={formState.isSubmitting} onClick={cancelHandler}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
              {t('modals.otp.buttons.confirmCode')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
